/**
 * Settings → AI's draft (slice 1j spec §3), pure: Model routing and Image
 * model edits stay in the tab until one Save, which is one `PUT
 * /api/computer/ai/routing` and one restart. What the bar says, and how a
 * draft follows a routing that changed underneath it. `routing-save.tsx`
 * only renders these.
 */
import type { ConnectStage, Me } from "../../app/me";
import { modelGroup, modelLabel, type Catalog } from "../setup/onboarding";
import { namesIn, routingOf, routingProblem, usableGroups, type Routing } from "./ai-view";
import { imageProblem, imageRoutingOf, type ImageRouting } from "./image-view";

type Ai = NonNullable<Me["computer"]>["ai"];

/** Everything one Save sends: the default, its fallbacks, and the image model. */
export type FullRouting = Routing & { image: ImageRouting };

/**
 * An open draft: what the cards show (`draft`), the routing it was made
 * against (`base`, and the `ai` it came from, to name what a change dropped),
 * and what the last rebase dropped.
 */
export type Draft = { base: FullRouting; baseAi: Ai; draft: FullRouting; dropped: string | null };

export function savedOf(ai: Ai): FullRouting {
  return { ...routingOf(ai), image: imageRoutingOf(ai) };
}

export function sameRouting(a: FullRouting, b: FullRouting): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * One edit (§3.1): applied to the draft, or to the saved routing for the
 * first. A draft back at the saved routing is no draft.
 */
export function editDraft(
  open: Draft | null,
  ai: Ai,
  edit: (routing: FullRouting) => FullRouting,
): Draft | null {
  const base = savedOf(ai);
  const draft = edit(open?.draft ?? base);
  if (sameRouting(draft, base)) return null;
  return { base, baseAi: open?.baseAi ?? ai, draft, dropped: open?.dropped ?? null };
}

/** What the server would refuse (§3.1): the routing's check, then the image model's. */
function draftProblem(draft: FullRouting, ai: Ai, catalog: Catalog | undefined) {
  return routingProblem(draft, ai, catalog) ?? imageProblem(draft.image, ai, catalog);
}

/** "Grok was disconnected, so Grok 4.7 was dropped." */
function droppedText(models: string[], before: Ai, catalog: Catalog | undefined): string | null {
  if (models.length === 0) return null;
  const groups = [...new Set(models.map(modelGroup))];
  const who = groups.map((g) => namesIn(before, g) || g).join(" and ");
  const names = models.map((m) => modelLabel(catalog, m) ?? m).join(" and ");
  const verb = groups.length === 1 && !who.includes(" and ") ? "was" : "were";
  return `${who} ${verb} disconnected, so ${names} ${models.length === 1 ? "was" : "were"} dropped.`;
}

/**
 * The routing changed underneath (§3.7): the draft keeps its own choices,
 * less every model no connected account serves now; a default that went
 * takes the new routing's, and an image model that went takes its next
 * fallback. Equal to the new routing, it's no draft.
 */
export function rebaseDraft(open: Draft, ai: Ai, catalog: Catalog | undefined): Draft | null {
  const base = savedOf(ai);
  const usable = usableGroups(ai);
  const served = (m: string) => usable.has(modelGroup(m));
  const { draft } = open;
  const images = draft.image ? [draft.image.model, ...draft.image.fallbacks] : [];
  const all = [draft.model, ...draft.fallbacks, ...images];
  const gone = [...new Set(all.filter((m): m is string => m !== null && !served(m)))];
  const model = draft.model && served(draft.model) ? draft.model : base.model;
  const fallbacks = draft.fallbacks.filter((m) => served(m) && m !== model);
  const next: FullRouting = { model, fallbacks, image: rebaseImage(draft.image, served) };
  if (sameRouting(next, base)) return null;
  const dropped = droppedText(gone, open.baseAi, catalog) ?? open.dropped ?? ELSEWHERE;
  return { base, baseAi: ai, draft: next, dropped };
}

/** A change that dropped nothing: another tab's save, say. */
const ELSEWHERE = "It was changed somewhere else.";

function rebaseImage(image: ImageRouting, served: (m: string) => boolean): ImageRouting {
  if (!image) return null;
  const [model, ...fallbacks] = [image.model, ...image.fallbacks].filter(served);
  return model ? { model, fallbacks } : null;
}

/** The request one Save sends (§3.3): `image` always, null when it's off. */
export function saveRequest(draft: FullRouting) {
  return { model: draft.model!, fallbacks: draft.fallbacks, image: draft.image };
}

export type SaveStepState = "done" | "current" | "todo";

/** The Saving bar's three steps (§3.4), from where the run is. */
export function saveSteps(
  stage: ConnectStage | undefined,
): { label: string; state: SaveStepState }[] {
  const at = stage === "starting" ? 2 : stage && stage !== "waking" ? 1 : 0;
  const state = (i: number): SaveStepState => (i < at ? "done" : i === at ? "current" : "todo");
  return [
    { label: "Wake your computer", state: state(0) },
    { label: "Answer current messages", state: state(1) },
    { label: "Restart your crew workspace", state: state(2) },
  ];
}

export type BarView =
  | { kind: "none" }
  | { kind: "saving"; steps: { label: string; state: SaveStepState }[] }
  | { kind: "saved" }
  | {
      kind: "draft";
      tone: "edited" | "busy" | "refused" | "conflict";
      title: string;
      text: string;
      canSave: boolean;
    };

const STILL_HERE = "Your changes are still here.";

/** "<hint>. Your changes are still here." */
const withStillHere = (text: string) => `${text.replace(/\.$/, "")}. ${STILL_HERE}`;

/**
 * What the bar says (§3.2–§3.7). A run in progress wins (this tab's save, or
 * another run while a draft is open), then a refusal of this very draft,
 * then a problem the server would refuse, then a rebase.
 */
export function barView(input: {
  open: Draft | null;
  ai: Ai;
  catalog: Catalog | undefined;
  saving: boolean;
  saved: boolean;
  /**
   * The last Save's failure, for the draft it sent. `retry`: it may work
   * again as it is (the network, the server), so Save stays on; a `400`
   * is off until the draft changes.
   */
  refused: { hint: string; draft: FullRouting; retry?: boolean } | null;
}): BarView {
  const { open, ai, saving, saved, refused } = input;
  if (saving) return { kind: "saving", steps: saveSteps(ai.stage) };
  if (!open) return saved ? { kind: "saved" } : { kind: "none" };
  if (ai.status === "connecting") {
    const text = `Save once it's done. ${STILL_HERE}`;
    return {
      kind: "draft",
      tone: "busy",
      title: "Your crew workspace is being updated",
      text,
      canSave: false,
    };
  }
  if (refused && sameRouting(refused.draft, open.draft)) {
    const text = withStillHere(refused.hint);
    const canSave = refused.retry === true;
    return { kind: "draft", tone: "refused", title: "Couldn't save", text, canSave };
  }
  const problem = draftProblem(open.draft, ai, input.catalog);
  if (problem)
    return {
      kind: "draft",
      tone: "edited",
      title: "Unsaved changes",
      text: problem,
      canSave: false,
    };
  if (open.dropped) {
    const title = "Your routing changed while you were editing";
    return {
      kind: "draft",
      tone: "conflict",
      title,
      text: `${open.dropped} ${STILL_HERE}`,
      canSave: true,
    };
  }
  const text = "Saving restarts your crew workspace once current messages are answered.";
  return { kind: "draft", tone: "edited", title: "Unsaved changes", text, canSave: true };
}
